/**
 * Pure display logic for the Quality Investigations panel.
 *
 * Everything derivable from a DoorInvestigationRead (or a list of them) lives
 * here as pure functions so it can be behavior-tested without rendering:
 * aging tiers for open asks, header-badge counts, filter chips, the FSM
 * transition button specs, date formatting, and badge labels. The .tsx panel
 * only wires these to markup (react-refresh/only-export-components keeps
 * non-component exports out of the component file).
 */
import type {
  CaseWorkState,
  DoorCaseIssueRead,
  DoorCaseTaskRead,
  DoorInvestigationRead,
  DoorThreadRead,
  InvestigationMetrics,
  InvestigationStatus,
  ThreadSourceTag,
} from '../../services/qcpInvestigations';

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const LOCALE = 'en-US' as const;

/* ------------------------------------------------------------------ */
/* Status labels & stepper                                             */
/* ------------------------------------------------------------------ */

export const STATUS_LABEL: Record<InvestigationStatus, string> = {
  investigating: 'Investigating',
  remediating: 'Remediating',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** The 3-step progress stepper (hidden entirely for cancelled). */
export const STEPPER_STEPS = [
  'Investigating',
  'Remediating',
  'Completed',
] as const;

/** 0-based stepper position for a status; null = no stepper (cancelled). */
export function stepperIndex(status: InvestigationStatus): number | null {
  switch (status) {
    case 'investigating':
      return 0;
    case 'remediating':
      return 1;
    case 'completed':
      return 2;
    case 'cancelled':
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* Aging of the oldest open ask                                        */
/* ------------------------------------------------------------------ */

export type AgingTier = 'none' | 'amber' | 'red';

export interface AgingInfo {
  /** Chip variant: none (<24h — no chip), amber (≥24h), red (≥72h). */
  tier: AgingTier;
  /** "waiting Nh" under a day, "waiting Nd" from then on; null = no asks. */
  label: string | null;
}

/** How long the oldest OPEN data request has been waiting, tiered. */
export function agingInfo(
  oldestOpenRequestAt: string | null,
  now: Date = new Date(),
): AgingInfo {
  if (!oldestOpenRequestAt) return { tier: 'none', label: null };
  const start = new Date(oldestOpenRequestAt).getTime();
  if (Number.isNaN(start)) return { tier: 'none', label: null };
  const diff = now.getTime() - start;
  if (diff < DAY_MS) {
    const hours = Math.max(0, Math.floor(diff / HOUR_MS));
    return { tier: 'none', label: `waiting ${hours}h` };
  }
  const days = Math.floor(diff / DAY_MS);
  return {
    tier: diff >= 3 * DAY_MS ? 'red' : 'amber',
    label: `waiting ${days}d`,
  };
}

/* ------------------------------------------------------------------ */
/* Header-badge aggregates                                             */
/* ------------------------------------------------------------------ */

/** Investigations still in flight (not completed, not cancelled). */
export function activeInvestigationCount(
  investigations: DoorInvestigationRead[],
): number {
  return investigations.filter(
    (i) => i.status !== 'completed' && i.status !== 'cancelled',
  ).length;
}

/** True when ANY investigation has an unviewed response (red dot). */
export function anyUnseenResponses(
  investigations: DoorInvestigationRead[],
): boolean {
  return investigations.some((i) => i.unseen_response_count > 0);
}

/* ------------------------------------------------------------------ */
/* Filter chips                                                        */
/* ------------------------------------------------------------------ */

/** The stage chips: the three lifecycle statuses plus `watching`, the
 *  stage Jess sees between remediation and close (kanban d91f705b) — a
 *  completed case whose effectiveness window is still open (the server's
 *  `court.side`), not a lifecycle status of its own. */
export type InvestigationFilter =
  | 'all'
  | 'investigating'
  | 'remediating'
  | 'watching'
  | 'completed';

export const FILTER_CHIPS: ReadonlyArray<{
  key: InvestigationFilter;
  label: string;
}> = [
  { key: 'all', label: 'All' },
  { key: 'investigating', label: 'Investigating' },
  { key: 'remediating', label: 'Remediating' },
  { key: 'watching', label: 'Waiting on effectiveness' },
  { key: 'completed', label: 'Completed' },
];

/** The investigations a filter chip shows ('all' includes cancelled too;
 *  'completed' is the lifecycle status, so it includes the watching ones). */
export function applyFilter(
  investigations: DoorInvestigationRead[],
  filter: InvestigationFilter,
): DoorInvestigationRead[] {
  if (filter === 'all') return investigations;
  if (filter === 'watching') return investigations.filter((i) => i.court.side === 'watching');
  return investigations.filter((i) => i.status === filter);
}

/** The count shown on a filter chip. */
export function filterCount(
  investigations: DoorInvestigationRead[],
  filter: InvestigationFilter,
): number {
  return applyFilter(investigations, filter).length;
}

/* ------------------------------------------------------------------ */
/* Transition buttons                                                  */
/* ------------------------------------------------------------------ */

/** Which draft field a transition sends along (drives the POST payload). */
export type TransitionNeed =
  | 'findings'
  | 'remediation_details'
  | 'reopen_note'
  | null;

export interface TransitionButtonSpec {
  to: InvestigationStatus;
  label: string;
  kind: 'primary' | 'ghost' | 'danger';
  needs: TransitionNeed;
  /** Ask window.confirm before firing (destructive: cancel). */
  confirm: boolean;
}

/**
 * Under "Ready to close": how a case is actually closed, by the stage
 * buttons at the bottom of the case (UX round 2026-10-27: "Mark ready to
 * close" was taken for the close, and no button said Close). Null once the
 * case is past remediation.
 */
export function closePathLine(status: InvestigationStatus): string | null {
  if (status === 'investigating') {
    return 'To close it, use the stage buttons at the bottom: “Investigation complete → Remediating” (needs the findings), then “Mark remediated → Completed” (needs what was done).';
  }
  if (status === 'remediating') {
    return 'To close it, use “Mark remediated → Completed” at the bottom (needs what was done).';
  }
  return null;
}

/**
 * The transition buttons for a detail view, derived ONLY from the server's
 * allowed_transitions (never from a client-side FSM copy). Order: forward
 * step first, back/reopen next, cancel last.
 */
export function transitionButtonSpecs(
  status: InvestigationStatus,
  allowedTransitions: string[],
): TransitionButtonSpec[] {
  const allowed = new Set(allowedTransitions);
  const specs: TransitionButtonSpec[] = [];
  if (status === 'investigating' && allowed.has('remediating')) {
    specs.push({
      to: 'remediating',
      label: 'Investigation complete → Remediating',
      kind: 'primary',
      needs: 'findings',
      confirm: false,
    });
  }
  if (status === 'remediating' && allowed.has('completed')) {
    specs.push({
      to: 'completed',
      label: 'Mark remediated → Completed',
      kind: 'primary',
      needs: 'remediation_details',
      confirm: false,
    });
  }
  if (status === 'remediating' && allowed.has('investigating')) {
    specs.push({
      to: 'investigating',
      label: '↩ Back to Investigating',
      kind: 'ghost',
      needs: null,
      confirm: false,
    });
  }
  if (status === 'completed' && allowed.has('investigating')) {
    specs.push({
      to: 'investigating',
      label: '↩ Reopen — issue recurred',
      kind: 'ghost',
      needs: 'reopen_note',
      confirm: false,
    });
  }
  if (status === 'cancelled' && allowed.has('investigating')) {
    specs.push({
      to: 'investigating',
      label: '↩ Reopen investigation',
      kind: 'ghost',
      needs: 'reopen_note',
      confirm: false,
    });
  }
  if (status !== 'cancelled' && allowed.has('cancelled')) {
    specs.push({
      to: 'cancelled',
      label: 'Cancel investigation',
      kind: 'danger',
      needs: null,
      confirm: true,
    });
  }
  return specs;
}

/* ------------------------------------------------------------------ */
/* Date formatting                                                     */
/* ------------------------------------------------------------------ */

function timeOfDay(date: Date): string {
  return date.toLocaleTimeString(LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isCalendarYesterday(date: Date, now: Date): boolean {
  const yesterday = new Date(now.getTime() - DAY_MS);
  return (
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()
  );
}

/**
 * Card/detail timestamps: relative under 24h ("just now", "12m ago",
 * "5h ago"), "Yesterday h:mm" for calendar-yesterday beyond that, and
 * "Mon D, h:mm" for anything older.
 */
export function formatInvestigationDate(
  iso: string,
  now: Date = new Date(),
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diff = now.getTime() - date.getTime();
  if (diff < DAY_MS) {
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(diff / HOUR_MS)}h ago`;
  }
  if (isCalendarYesterday(date, now)) return `Yesterday ${timeOfDay(date)}`;
  return `${date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
  })}, ${timeOfDay(date)}`;
}

/* ------------------------------------------------------------------ */
/* Badge labels                                                        */
/* ------------------------------------------------------------------ */

/** "📨 N open ask(s)" red-tinted badge, or null when nothing is open. */
export function openAsksLabel(openRequestCount: number): string | null {
  if (openRequestCount <= 0) return null;
  return `📨 ${openRequestCount} open ask${openRequestCount === 1 ? '' : 's'}`;
}

/** "📥 response — NEW" green badge, or null when everything's been seen. */
export function unseenResponseLabel(
  unseenResponseCount: number,
): string | null {
  return unseenResponseCount > 0 ? '📥 response — NEW' : null;
}

/** "💬 N conversation(s)" badge, or null when none are linked. */
export function conversationsLabel(conversationCount: number): string | null {
  if (conversationCount <= 0) return null;
  return `💬 ${conversationCount} conversation${
    conversationCount === 1 ? '' : 's'
  }`;
}

/** "↩ recurred ×N" badge, or null when never reopened. */
export function recurredLabel(reopenedCount: number): string | null {
  return reopenedCount > 0 ? `↩ recurred ×${reopenedCount}` : null;
}

/* ------------------------------------------------------------------ */
/* Resurrection links (an old, rectified issue coming back)            */
/* ------------------------------------------------------------------ */

export interface ResurrectionChipSpec {
  /** Stable render key (the other case's id + direction). */
  key: string;
  /** 'resurrects' = old case this one rediscovers; 'rediscovered' = later
   *  case that rediscovered this one. Styling derives from this. */
  kind: 'resurrects' | 'rediscovered';
  label: string;
  /** Tooltip: the other case's title, plus the linker's why-note. */
  title: string;
}

/** Header chips for a case's resurrection links, both directions. */
export function resurrectionChipSpecs(
  investigation: DoorInvestigationRead,
): ResurrectionChipSpec[] {
  const specs: ResurrectionChipSpec[] = [];
  for (const link of investigation.resurrects ?? []) {
    specs.push({
      key: `resurrects-${link.investigation_id}`,
      kind: 'resurrects',
      label: `↻ resurrects ${link.investigation_no}`,
      title: link.note ? `${link.title} — ${link.note}` : link.title,
    });
  }
  for (const link of investigation.resurrected_by ?? []) {
    specs.push({
      key: `rediscovered-${link.investigation_id}`,
      kind: 'rediscovered',
      label: `⚠ rediscovered as ${link.investigation_no}`,
      title: link.note ? `${link.title} — ${link.note}` : link.title,
    });
  }
  return specs;
}

/**
 * Card badge for the list: the strongest resurrection fact about a case, or
 * null when it has no links. Being a resurrection ("↻ resurrection of
 * INV-…") outranks having been rediscovered — the new case is where the
 * work is.
 */
export function resurrectionBadge(
  investigation: DoorInvestigationRead,
): string | null {
  const resurrects = investigation.resurrects ?? [];
  if (resurrects.length > 0) {
    return `↻ resurrection of ${resurrects
      .map((link) => link.investigation_no)
      .join(', ')}`;
  }
  const rediscoveredBy = investigation.resurrected_by ?? [];
  if (rediscoveredBy.length > 0) {
    return `⚠ rediscovered ×${rediscoveredBy.length}`;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Work state (whose court) & the outstanding-task checklist           */
/* ------------------------------------------------------------------ */

/** Chip/pill text for the whose-court axis. */
export const WORK_STATE_LABEL: Record<CaseWorkState, string> = {
  outstanding_on_us: '🔧 On us',
  waiting_on_customer: '⏳ Waiting on customer',
  waiting_on_colleague: '👥 Waiting on a colleague',
  ready_to_close: '🏁 Ready to close',
};

/** The detail view's whose-court selector: the current one highlighted,
 *  clickability from allowed_work_states. A state the server never offers
 *  (a server from before "waiting on a colleague") is not shown at all. */
export const WORK_STATE_OPTIONS: ReadonlyArray<{
  key: CaseWorkState;
  label: string;
  hint: string;
}> = [
  {
    key: 'outstanding_on_us',
    label: WORK_STATE_LABEL.outstanding_on_us,
    hint: 'We owe the next step',
  },
  {
    key: 'waiting_on_customer',
    label: WORK_STATE_LABEL.waiting_on_customer,
    hint: "Ball is in the customer's court",
  },
  {
    key: 'waiting_on_colleague',
    label: WORK_STATE_LABEL.waiting_on_colleague,
    hint: 'A teammate owes the next step: the lab, customer service, the warehouse',
  },
  {
    key: 'ready_to_close',
    label: WORK_STATE_LABEL.ready_to_close,
    hint: 'Nothing material left — just needs closed out',
  },
];

/** In flight (not completed, not cancelled) — the whose-court axis only
 *  means something while the case is live. */
export function isActiveCase(investigation: DoorInvestigationRead): boolean {
  return (
    investigation.status !== 'completed' && investigation.status !== 'cancelled'
  );
}

export type WorkStateFilter = 'any' | CaseWorkState;

/** Second chip row on the Case files tab — filter by whose court. */
export const WORK_STATE_CHIPS: ReadonlyArray<{
  key: WorkStateFilter;
  label: string;
}> = [
  { key: 'any', label: 'Any court' },
  { key: 'outstanding_on_us', label: 'On us' },
  { key: 'waiting_on_customer', label: 'On the customer' },
  { key: 'waiting_on_colleague', label: 'On a colleague' },
  { key: 'ready_to_close', label: 'Ready to close' },
];

/** Work-state chips only ever narrow ACTIVE cases ('any' passes everything
 *  through — completed/cancelled visibility stays the status filter's job). */
export function applyWorkStateFilter(
  investigations: DoorInvestigationRead[],
  filter: WorkStateFilter,
): DoorInvestigationRead[] {
  if (filter === 'any') return investigations;
  return investigations.filter(
    (i) => isActiveCase(i) && i.work_state === filter,
  );
}

/** The count shown on a work-state chip. */
export function workStateFilterCount(
  investigations: DoorInvestigationRead[],
  filter: WorkStateFilter,
): number {
  return applyWorkStateFilter(investigations, filter).length;
}

export type CaseSortMode = 'newest' | 'ours_first';

const WORK_STATE_ORDER: Record<CaseWorkState, number> = {
  outstanding_on_us: 0,
  ready_to_close: 1,
  waiting_on_customer: 2,
  waiting_on_colleague: 2,
};

/**
 * 'newest' keeps the server order (newest first). 'ours_first' is Jess's
 * work-the-list order: active cases in our court first, then ready-to-close,
 * then waiting-on-customer, closed cases last; ties break by the oldest open
 * our-side task (longest-stuck first), then newest case first.
 */
export function sortCases(
  investigations: DoorInvestigationRead[],
  mode: CaseSortMode,
): DoorInvestigationRead[] {
  if (mode === 'newest') return investigations;
  return [...investigations].sort((a, b) => {
    const activeDiff = Number(isActiveCase(b)) - Number(isActiveCase(a));
    if (activeDiff !== 0) return activeDiff;
    const stateDiff =
      (WORK_STATE_ORDER[a.work_state] ?? 3) -
      (WORK_STATE_ORDER[b.work_state] ?? 3);
    if (stateDiff !== 0) return stateDiff;
    const aOldest = a.oldest_open_task_at ?? '';
    const bOldest = b.oldest_open_task_at ?? '';
    if (aOldest !== bOldest) {
      // A case WITH an old open task outranks one with none.
      if (!aOldest) return 1;
      if (!bOldest) return -1;
      return aOldest.localeCompare(bOldest);
    }
    return b.created_at.localeCompare(a.created_at);
  });
}

/** "🔧 N on us" badge, or null when our desk is clear. */
export function ourTasksLabel(openTaskCountUs: number): string | null {
  if (openTaskCountUs <= 0) return null;
  return `🔧 ${openTaskCountUs} on us`;
}

/** "⏳ N on customer" badge, or null when nothing waits on them. */
export function customerTasksLabel(
  openTaskCountCustomer: number,
): string | null {
  if (openTaskCountCustomer <= 0) return null;
  return `⏳ ${openTaskCountCustomer} on customer`;
}

/** The close-out nudge — Jess: many old cases "just need closed out more so
 *  than anything, and this will be a good reminder". */
export function closeOutNudgeLabel(
  investigation: DoorInvestigationRead,
): string | null {
  return isActiveCase(investigation) &&
    investigation.work_state === 'ready_to_close'
    ? '🏁 just needs closed out'
    : null;
}

/** The checklist as shown: cancelled items are hidden, order preserved. */
export function visibleTasks(tasks: DoorCaseTaskRead[]): DoorCaseTaskRead[] {
  return tasks.filter((t) => t.status !== 'cancelled');
}

/** What clicking the checkbox means, or null when the FSM forbids it. */
export function taskToggleTarget(
  task: DoorCaseTaskRead,
): 'open' | 'done' | null {
  const target = task.status === 'done' ? 'open' : 'done';
  return task.allowed_statuses.includes(target) ? target : null;
}

/** One row of the aggregate "everything still outstanding on our end" view. */
export interface OutstandingTaskRow {
  investigation: DoorInvestigationRead;
  task: DoorCaseTaskRead;
}

/**
 * Every OPEN our-side checklist item across the ACTIVE cases bucket,
 * longest-outstanding first — Jess's cross-case to-do list.
 */
export function outstandingOurTasks(
  investigations: DoorInvestigationRead[],
): OutstandingTaskRow[] {
  const rows: OutstandingTaskRow[] = [];
  for (const investigation of investigations) {
    if (!isActiveCase(investigation)) continue;
    for (const task of investigation.tasks) {
      if (task.status === 'open' && task.owner === 'us') {
        rows.push({ investigation, task });
      }
    }
  }
  return rows.sort((a, b) => a.task.created_at.localeCompare(b.task.created_at));
}

/** Active cases flagged ready-to-close — the Outstanding tab's nudge list. */
export function readyToCloseCases(
  investigations: DoorInvestigationRead[],
): DoorInvestigationRead[] {
  return investigations.filter(
    (i) => isActiveCase(i) && i.work_state === 'ready_to_close',
  );
}

/** The Outstanding tab's count: our open items + close-out nudges. */
export function outstandingBadgeCount(
  investigations: DoorInvestigationRead[],
): number {
  return (
    outstandingOurTasks(investigations).length +
    readyToCloseCases(investigations).length
  );
}

/* ------------------------------------------------------------------ */
/* The sales-site complaint behind a case (Jess, 2026-09-08)           */
/* ------------------------------------------------------------------ */

/** The complaints behind a case — `[]` for older backends that omit them. */
export function linkedIssues(
  investigation: Pick<DoorInvestigationRead, 'linked_issues'>,
): DoorCaseIssueRead[] {
  return investigation.linked_issues ?? [];
}

/**
 * "LEG-00263 · TUSCO01" — the chip that tells Jess, on a cross-case row,
 * which sales-site complaint a case is about. Null when none is linked.
 */
export function complaintChipLabel(
  investigation: Pick<DoorInvestigationRead, 'linked_issues'>,
): string | null {
  const issues = linkedIssues(investigation);
  if (issues.length === 0) return null;
  const first = issues[0];
  const head = first.customer_no ? `${first.issue_no} · ${first.customer_no}` : first.issue_no;
  return issues.length > 1 ? `${head} +${issues.length - 1}` : head;
}

/**
 * The residual state a completed case cannot clear: sales-site complaints the
 * portal has no write path into yet. Jess, QT-10021 (2026-09-14): "I have
 * completed this, why is it still not closed". Scott's wording, 2026-09-15.
 * Kept here so the case file and the Done thread say exactly the same thing.
 */
export const EXTERNAL_CLOSURE_NOTICE =
  'Still needs to be confirmed in Sage - automatic closure coming soon';

export interface ExternalClosureNotice {
  /** The LEG/QI numbers still open on the sales site, in link order. */
  issueNos: string[];
  text: string;
}

/**
 * The banner for a set of resolved complaints (case or thread): null unless
 * at least one is still pending on the sales site. Prefers the backend's own
 * wording when it sent one, so a copy change never needs a frontend deploy.
 */
export function externalClosureNoticeForIssues(
  issues: ReadonlyArray<Pick<DoorCaseIssueRead, 'issue_no' | 'external_closure_pending'>>,
  text: string | null | undefined = EXTERNAL_CLOSURE_NOTICE,
): ExternalClosureNotice | null {
  const issueNos = issues.filter((i) => i.external_closure_pending).map((i) => i.issue_no);
  if (issueNos.length === 0) return null;
  return { issueNos, text: text || EXTERNAL_CLOSURE_NOTICE };
}

/**
 * The completed-case banner: only once the case is Completed (an open case
 * is still being worked, nothing has been "closed from our portal" yet).
 * Trusts `pending_external_closures` when the backend sent it, and derives
 * from the complaints' own flags otherwise.
 */
export function externalClosureNotice(
  investigation: Pick<
    DoorInvestigationRead,
    'status' | 'linked_issues' | 'pending_external_closures' | 'external_closure_notice'
  >,
): ExternalClosureNotice | null {
  if (investigation.status !== 'completed') return null;
  const text = investigation.external_closure_notice ?? EXTERNAL_CLOSURE_NOTICE;
  if (investigation.pending_external_closures !== undefined) {
    const issueNos = investigation.pending_external_closures;
    return issueNos.length > 0 ? { issueNos, text } : null;
  }
  return externalClosureNoticeForIssues(linkedIssues(investigation), text);
}

export interface ComplaintFieldRow {
  label: string;
  value: string;
}

/**
 * The complaint form's filled-in fields as label/value rows, empties dropped
 * — so the case file shows what sales actually entered, nothing blank.
 */
export function complaintFieldRows(issue: DoorCaseIssueRead): ComplaintFieldRow[] {
  const rows: Array<[string, string | null | undefined]> = [
    ['Customer', issue.customer_no],
    ['Status', issue.status ? issue.status.replace(/_/g, ' ') : null],
    ['Category', issue.category],
    ['Sales rep', issue.csr_contact],
    ['Products', issue.product_skus.length ? issue.product_skus.join(', ') : null],
    ['Lot', issue.lot_number],
    ['PO', issue.po_number],
    ['Root cause', issue.root_cause],
    [
      'Root cause category',
      [issue.root_cause_category, issue.root_cause_subcategory]
        .filter((v): v is string => !!v)
        .join(' › ') || null,
    ],
    ['Department', issue.department_responsible],
    ['Assigned to', issue.assigned_to],
    ['Cost of damages', issue.cost_of_damages],
    ['Resolve steps', issue.resolution],
    [
      'Attachments',
      issue.attachment_count > 0
        ? `${issue.attachment_count} on the sales site`
        : null,
    ],
  ];
  return rows
    .filter((r): r is [string, string] => typeof r[1] === 'string' && r[1].trim().length > 0)
    .map(([label, value]) => ({ label, value: value.trim() }));
}

/* ------------------------------------------------------------------ */
/* Remediation drafts & transition errors                              */
/* ------------------------------------------------------------------ */

/** One remediation action per line: trims and drops empty lines. */
export function splitRemediationLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * User-facing message for a failed transition POST: 400 carries the FSM
 * gate's refusal detail ("Findings are required…"), 409 an illegal-transition
 * explanation; anything else falls back to a generic retry line.
 */
export function transitionErrorMessage(error: unknown): string {
  const response = (
    error as {
      response?: { status?: number; data?: { detail?: unknown } };
    }
  )?.response;
  const detail =
    typeof response?.data?.detail === 'string' ? response.data.detail : null;
  if (response?.status === 400) {
    return detail ?? 'That step needs more information before it can happen.';
  }
  if (response?.status === 409) {
    return (
      detail ??
      'That transition is no longer allowed — the investigation may have changed.'
    );
  }
  return 'Could not update the investigation — please try again.';
}

/**
 * "Could not <action>. <why>" — the error for one change control, naming
 * what failed (fan-out finding 3: one shared "Could not update the thread"
 * looked the same whichever button was pressed). The server's own reason
 * (a 400 gate or a 409 conflict) wins; otherwise say nothing was changed.
 */
export function failedActionMessage(action: string, error: unknown): string {
  const response = (
    error as {
      response?: { status?: number; data?: { detail?: unknown } };
    }
  )?.response;
  const detail =
    typeof response?.data?.detail === 'string' ? response.data.detail.trim() : '';
  if (detail) return `Could not ${action}. ${detail}`;
  if (response?.status === 409) {
    return `Could not ${action}. Someone may have changed it first; reload to see where it stands.`;
  }
  return `Could not ${action}. Nothing was changed. Please try again.`;
}

/* ------------------------------------------------------------------ */
/* Tracked signal threads (Merlin's proactive routing)                 */
/* ------------------------------------------------------------------ */

/** Severity chip text (Agent 1's judgment). */
export const THREAD_SEVERITY_LABEL: Record<
  DoorThreadRead['severity'],
  string
> = {
  critical: 'Critical',
  high: 'High',
  watch: 'Watch',
};

/**
 * The source tag Jess asked for (2026-08-18): sales-site complaints are
 * confirmed problems; signal-only threads are "take a look" suspicions.
 */
export const SOURCE_TAG_LABEL: Record<ThreadSourceTag, string> = {
  sales_site: '✔ Confirmed — sales-site complaint',
  signal_suspected: '👀 Possible issue — take a look',
};

/** "N signals" evidence badge, or null when the agent linked none. */
export function signalCountLabel(count: number): string | null {
  if (count <= 0) return null;
  return `${count} signal${count === 1 ? '' : 's'}`;
}

/** "→ routed to X" badge, or null while unrouted. */
export function routedToLabel(routedTo: string | null): string | null {
  return routedTo ? `→ ${routedTo}` : null;
}

const SEVERITY_ORDER: Record<DoorThreadRead['severity'], number> = {
  critical: 0,
  high: 1,
  watch: 2,
};

/** Worst severity first, then most recently touched. */
export function sortThreads(threads: DoorThreadRead[]): DoorThreadRead[] {
  return [...threads].sort((a, b) => {
    const bySeverity =
      (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3);
    if (bySeverity !== 0) return bySeverity;
    return b.updated_at.localeCompare(a.updated_at);
  });
}

/**
 * User-facing message for a failed thread action POST. 409 = the thread moved
 * on (another session resolved it); 400 = the server wants more (a reason /
 * a person's name).
 */
export function threadActionErrorMessage(error: unknown): string {
  const response = (
    error as {
      response?: { status?: number; data?: { detail?: unknown } };
    }
  )?.response;
  const detail =
    typeof response?.data?.detail === 'string' ? response.data.detail : null;
  if (response?.status === 400) {
    return detail ?? 'That action needs more information.';
  }
  if (response?.status === 409) {
    return detail ?? 'This thread was already handled.';
  }
  return 'Could not update the thread — please try again.';
}

/* ------------------------------------------------------------------ */
/* Cross-case metrics strip (GET /investigations/metrics)              */
/* ------------------------------------------------------------------ */

export interface MetricsStripItem {
  key: string;
  label: string;
  value: string;
  /** 'alert' renders the value in the attention color. */
  tone?: 'alert';
}

/**
 * The headline stat tiles, in display order. Zero-valued load tiles are
 * dropped (an empty strip cell says nothing); the court split always shows
 * so "everything is with the customer" is visible as On us 0.
 */
export function metricsStripItems(
  metrics: InvestigationMetrics,
): MetricsStripItem[] {
  const items: MetricsStripItem[] = [
    { key: 'active', label: 'Active', value: String(metrics.active) },
    {
      key: 'on_us',
      label: 'On us',
      value: String(metrics.by_work_state.outstanding_on_us ?? 0),
    },
    {
      key: 'waiting',
      label: 'Waiting on customer',
      value: String(metrics.by_work_state.waiting_on_customer ?? 0),
    },
    {
      key: 'ready',
      label: 'Ready to close',
      value: String(metrics.by_work_state.ready_to_close ?? 0),
    },
  ];
  if (metrics.open_asks > 0) {
    items.push({
      key: 'asks',
      label: 'Open asks',
      value: String(metrics.open_asks),
    });
  }
  if (metrics.reopened_cases > 0) {
    items.push({
      key: 'recurred',
      label: 'Recurred',
      value: String(metrics.reopened_cases),
      tone: 'alert',
    });
  }
  if (metrics.median_days_to_complete !== null) {
    items.push({
      key: 'median',
      label: 'Median days to close',
      value: String(metrics.median_days_to_complete),
    });
  }
  return items;
}

/**
 * Top-customer / top-SKU chips, e.g. "CARRO09 ×3". Sliced to the top 3 per
 * dimension so the strip stays one glance; the server already sends at most
 * its own top 5, worst-first.
 */
export function metricsSliceChips(
  metrics: InvestigationMetrics,
): Array<{ key: string; label: string }> {
  const chips: Array<{ key: string; label: string }> = [];
  for (const slice of metrics.by_customer.slice(0, 3)) {
    chips.push({
      key: `customer:${slice.key}`,
      label: `${slice.key} ×${slice.count}`,
    });
  }
  for (const slice of metrics.by_sku.slice(0, 3)) {
    chips.push({
      key: `sku:${slice.key}`,
      label: `${slice.key} ×${slice.count}`,
    });
  }
  return chips;
}

/** Nudge when active cases carry no keys and are invisible to the slices. */
export function untaggedNudgeLabel(
  metrics: InvestigationMetrics,
): string | null {
  if (metrics.untagged_active <= 0) return null;
  const plural = metrics.untagged_active === 1 ? 'case has' : 'cases have';
  return `${metrics.untagged_active} active ${plural} no case keys — tag the customer/product so they count in the breakdowns.`;
}

/** The whose-court options this case can show: its current state and every
 *  state the server allows from it, in the fixed option order. */
export function workStateOptionsFor(
  investigation: Pick<DoorInvestigationRead, 'work_state' | 'allowed_work_states'>,
): typeof WORK_STATE_OPTIONS {
  return WORK_STATE_OPTIONS.filter(
    (opt) =>
      opt.key === investigation.work_state ||
      investigation.allowed_work_states.includes(opt.key),
  );
}

/** True when any case is in, or could move to, a given court — the Cases
 *  filter hides a court this server does not know (the colleague court on an
 *  older server). */
export function courtKnown(
  investigations: Pick<DoorInvestigationRead, 'work_state' | 'allowed_work_states'>[],
  court: CaseWorkState,
): boolean {
  return investigations.some(
    (i) => i.work_state === court || i.allowed_work_states.includes(court),
  );
}

/** Who wrote a history entry, as the timeline shows it (turn 1: "Case
 *  opened" with no author read as something done behind your back). The
 *  audit trail records the kind of actor; names are not on the wire. */
export function actorWord(kind: 'merlin' | 'human' | 'system' | string): string {
  if (kind === 'merlin') return 'Merlin';
  if (kind === 'human') return 'A person';
  return 'Automatic';
}

/* ------------------------------------------------------------------ */
/* Linked conversations (approved 2026-07-24 mockup, kanban b32f10ce)   */
/* ------------------------------------------------------------------ */

export interface LinkedConversationRow {
  conversationId: string;
  /** Printable label — the drawer title, or a generic fallback. */
  title: string;
  isOriginating: boolean;
  attachedAt: string | null;
}

export const UNTITLED_CONVERSATION = 'Untitled conversation';

/**
 * One row per conversation attached to the case, oldest-attached first.
 * Prefers the titled `linked_conversations` read; an older backend that only
 * sends `conversation_ids` still gets a row per id (generic label) so the
 * way to each conversation is never lost.
 */
export function linkedConversationRows(
  investigation: Pick<
    DoorInvestigationRead,
    'linked_conversations' | 'conversation_ids' | 'originating_conversation_id'
  >,
): LinkedConversationRow[] {
  const linked = investigation.linked_conversations;
  if (linked && linked.length > 0) {
    return linked.map((c) => ({
      conversationId: c.conversation_id,
      title: c.title?.trim() || UNTITLED_CONVERSATION,
      isOriginating: c.is_originating,
      attachedAt: c.attached_at,
    }));
  }
  return investigation.conversation_ids.map((id) => ({
    conversationId: id,
    title: UNTITLED_CONVERSATION,
    isOriginating: id === investigation.originating_conversation_id,
    attachedAt: null,
  }));
}

/** "Case opened from this conversation" / "Attached Mon D, h:mm". */
export function linkedConversationMeta(
  row: LinkedConversationRow,
  now: Date = new Date(),
): string {
  if (row.isOriginating) return 'Case opened from this conversation';
  return row.attachedAt
    ? `Attached ${formatInvestigationDate(row.attachedAt, now)}`
    : 'Attached';
}

/**
 * Whether the case file should list its conversations at all. The one
 * conversation already showing beside the case (the host's pane) is not worth
 * a row of its own (fan-out finding 9: the count is information, not a
 * control); the list earns its place once there is somewhere else to go.
 */
export function showLinkedConversations(
  rows: LinkedConversationRow[],
  activeConversationId: string | null | undefined,
): boolean {
  if (rows.length === 0) return false;
  return !(rows.length === 1 && rows[0].conversationId === activeConversationId);
}
